import React from 'react';

import CustomMultiSelect from 'src/components/MessengerSettings/CustomMultiSelect';
import { chatgenieAPI } from 'src/config/api';
import RadioGroup from 'src/pages/DeploymentCenter/components/RadioGroup';
import { Button, Checkbox, RadioButton } from '@Thread-Magic/jasmine';
import Switcher from 'src/components/Switcher';
import SimpleInput from 'src/components/SimpleInput';
import PageHolder from 'src/components/PageHolder';
import { FiArrowRight } from 'react-icons/fi';
import { BUSINESS_MESSENGER_URL } from 'src/constants';
import useAuthUser from 'src/hooks/useAuthUser';
import useAgentSettings from './Tabs/hook';
import { AgentHeader, DeactivationTimeoutWrapper, SettingsWrapper } from './style';
import { SettingsBox, SettingsBoxContent, SettingsBoxHeader } from '../components/style';

const getCompanies = (query, page) =>
  chatgenieAPI.getCompanies({ query, page, normalizer: res => res.data });

const usageTypes = [
  {
    value: 'all',
    label: 'Use for chat and email'
  },
  {
    value: 'chat',
    label: 'Use only for chat'
  },
  {
    value: 'email',
    label: 'Use only for email'
  }
];

const Settings = () => {
  const { isLoading, settings, updateAgentSettings, saveAgentSettings, isUpdating } =
    useAgentSettings();
  const { appId } = useAuthUser();

  const getBoards = (query, page) =>
    chatgenieAPI.getBoards({ query, page, normalizer: res => res.data });

  if (isLoading) {
    return <PageHolder fullHeight isLoading />;
  }

  return (
    <SettingsWrapper>
      <AgentHeader>
        <div>
          <h5>Settings</h5>
        </div>
      </AgentHeader>
      <SettingsBox>
        <SettingsBoxHeader>
          <h6>Enable Magic Agents </h6>
          <Switcher
            id="is-magic-agent-enabled"
            checked={settings.enabled ?? false}
            onChange={() =>
              updateAgentSettings({
                enabled: !settings.enabled
              })
            }
            width={50}
            controlText={{ checked: '', unchecked: '' }}
            isLoading={isLoading}
          />
        </SettingsBoxHeader>
        <SettingsBoxContent>
          <div>
            <p />
            <RadioGroup
              value={settings.isAllClients ? 'all' : 'specific'}
              options={[
                { label: 'All managed clients', value: 'all' },
                { label: 'Specific clients', value: 'specific' }
              ]}
              onChange={value => {
                const isAllClients = value === 'all';
                updateAgentSettings({
                  isAllClients
                });
              }}
            />
            {!settings.isAllClients && (
              <CustomMultiSelect
                name="included-customers"
                value={settings?.customerActivations}
                options={getCompanies}
                onChange={option => updateAgentSettings({ customerActivations: option })}
                componentType="paginate"
                isLoading={isLoading}
              />
            )}
          </div>

          <div>
            <p>Exclude boards</p>
            <CustomMultiSelect
              name="excluded-boards"
              value={settings?.boardExclusions}
              options={getBoards}
              onChange={option => updateAgentSettings({ boardExclusions: option })}
              componentType="paginate"
              isLoading={isLoading}
            />
          </div>
          <div>
            <p>Choose type of support</p>
            {usageTypes.map(({ value, label }) => (
              <RadioButton
                onChange={e => updateAgentSettings({ usageType: e.target.value })}
                id="magic-agent-usage-type"
                checked={settings?.usageType === value}
                value={value}
                key={value}
              >
                {label}
              </RadioButton>
            ))}
          </div>
          <div>
            <p>When there is no response from contact</p>
            <DeactivationTimeoutWrapper>
              Pass to technician after{' '}
              <SimpleInput
                value={settings.deactivationTimeout || ''}
                type="number"
                onChange={e => {
                  const { value } = e.target;
                  updateAgentSettings({ deactivationTimeout: value });
                }}
              />{' '}
              minutes
            </DeactivationTimeoutWrapper>
          </div>
          <div>
            <p>Rerun Magic AI actions</p>
            <Checkbox
              id="re-run-action"
              checked={settings.rerunMagicActions}
              onChange={() =>
                updateAgentSettings({ rerunMagicActions: !settings.rerunMagicActions })
              }
            >
              Rerun Auto-prioritization, Auto-categorization & Magic Title when thread is triaged
            </Checkbox>
          </div>
        </SettingsBoxContent>
      </SettingsBox>
      <SettingsBox>
        <SettingsBoxHeader>
          <div>
            <h6>Status mapping</h6>
            <p>Map your statuses and configure which statuses Magic Agents should use.</p>
          </div>
          <Button
            onClick={() =>
              window.open(`${BUSINESS_MESSENGER_URL}/preferences?app_id=${appId}`, '_blank')
            }
          >
            Configure &nbsp; <FiArrowRight />
          </Button>
        </SettingsBoxHeader>
      </SettingsBox>
      <div>
        <Button size="large" isLoader={isUpdating} onClick={saveAgentSettings}>
          Save
        </Button>
      </div>
    </SettingsWrapper>
  );
};

export default Settings;
